import React, { useMemo } from "react";
import { LatLngTuple } from "leaflet";
import { CircleMarker, Tooltip } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import MapPopup from "components/MapPopup";
import { connect } from "react-redux";
import styled from "@emotion/styled";

const SpaceLabelsTooltip = styled(Tooltip)`
  &.leaflet-tooltip {
    background-color: transparent !important;
    border-color: transparent !important;
    box-shadow: none;
    color: white;
    font-size: 10px;
  }

  &.leaflet-tooltip-left:before {
    border-left-color: transparent !important;
  }
  &.leaflet-tooltip-right:before {
    border-right-color: transparent !important;
  }
`;

type Props = {
  hover?: boolean;
  selected?: boolean;
  withPopup?: boolean;
  zoom: number;
  center: LatLngTuple;
  onPopupClose?: Function;
  onPopupOpen?: Function;
  options: {
    [key: string]: string | {} | boolean;
    meta_data: {
      area: { type: string };
      [key: string]: string | {} | boolean;
    };
    radius: number;
    timestamp: number;
    fill_color: { [key: string]: string };
    border_color: { [key: string]: string };
    border_weight: { [key: string]: number };
  };
  fillColor?: string;
  mapConfig?: { [key: string]: {} };
};

function MapCarMarker({
  hover,
  selected,
  center,
  onPopupClose,
  onPopupOpen,
  options,
  withPopup,
  fillColor: fillColorFromProps,
  zoom,
  mapConfig,
}: Props): JSX.Element {
  const calculatedMarkerType = useMemo(() => {
    if (selected) {
      return "selected";
    }
    if (hover) {
      return "hover";
    }
    return options?.meta_data?.area?.type || "normal";
  }, [options, selected, hover]);

  const circleRadius = useMemo(() => {
    const defaultRadius =
      MapConfig?.marker?.radius?.[`zoom${zoom}`] ||
      MapConfig?.marker?.radius?.default ||
      2;
    // @ts-ignore
    const radius = options?.radius?.[`zoom${zoom}`];

    return radius || defaultRadius;
  }, [options, zoom]);

  const fillColor = useMemo(() => {
    const markerType = options?.type || "default"; // probably_car ; car ; spot ; area
    const defaultFillColor = MapConfig?.marker?.fillColor?.default || "#EB1547";
    const markerFillColorFromConfig =
      MapConfig?.marker?.fillColor?.[`${markerType}`] || defaultFillColor;
    let markerFillColor = !!options?.is_locked
      ? options?.fill_color?.is_locked ??
        MapConfig?.marker?.fillColor?.unavailable
      : options?.fill_color?.[`${markerType}`] || markerFillColorFromConfig;

    if (options?.is_awol === true && markerType !== "unavailable") {
      markerFillColor = options?.fill_color?.awol_spot || markerFillColor;
    }

    return fillColorFromProps || markerFillColor;
  }, [fillColorFromProps, options]);

  const color: string = useMemo(() => {
    const defaultColor = MapConfig?.marker?.color?.default;
    const markerColorFromConfig =
      MapConfig?.marker?.color?.[`${calculatedMarkerType}`] || defaultColor; // Try to get from config File
    const markerColor =
      options?.border_color?.[`${calculatedMarkerType}`] ||
      markerColorFromConfig; // Try to get from API config

    return markerColor;
  }, [options, calculatedMarkerType]);

  const weight: number = useMemo(() => {
    const defaultWeight = MapConfig?.marker?.weight?.default || 0;
    const markerWeightFromConfig =
      MapConfig?.marker?.weight?.[`${calculatedMarkerType}`] || defaultWeight;
    const markerWeight =
      options?.border_weight?.[`${calculatedMarkerType}`] ||
      markerWeightFromConfig;

    return markerWeight;
  }, [options, calculatedMarkerType]);

  return (
    <CircleMarker
      center={center}
      radius={circleRadius}
      fillColor={fillColor}
      fillOpacity={1}
      weight={weight}
      color={color}
    >
      {mapConfig?.spaceLabelsDisplayed &&
        // @ts-ignore
        options?.meta_data?.client?.label && (
          <SpaceLabelsTooltip
            permanent
            opacity={1}
            direction="center"
            className="text"
            // @ts-ignore
            content={options?.meta_data?.client?.label}
            offset={[
              // @ts-ignore
              options?.meta_data?.client?.label?.length > 1 ? 0 : -0.5,
              0,
            ]}
          >
            {
              // @ts-ignore
              options?.meta_data?.client?.label
            }
          </SpaceLabelsTooltip>
        )}
      {withPopup && options?.type !== "unavailable" ? (
        <MapPopup
          // @ts-ignore
          center={center}
          isOpen={selected}
          onClose={onPopupClose}
          onOpen={onPopupOpen}
          layerType={options.type}
          markerType={options.area_type}
          arrivalTime={options.arrival_time}
          duration={options.duration}
          leavingTime={options.leaving_time}
          totalDuration={options.total_duration}
          availableFrom={options.available_from}
          availableTo={options.available_to}
          timestamp={options.timestamp}
          size={options.size}
          type={options?.meta_data?.area?.type}
          isLocked={options?.is_locked}
          canBeLocked={options?.meta_data.can_be_locked}
          uid={options?.uid}
        />
      ) : null}
    </CircleMarker>
  );
}

MapCarMarker.defaultProps = {
  options: {},
  hover: false,
  selected: false,
  withPopup: true,
};

const mapStateToProps = (state: any) => ({
  mapConfig: state.Map?.config,
});

export default connect(mapStateToProps)(MapCarMarker);
